import React, { useState } from "react";
import { useT } from "../i18n/LanguageContext";

/**
 * Interactive Sanity Framework diagram.
 * Center: SANITY. Three connected pillars: Body, Mind, Spirit.
 * Hover/tap a pillar -> reveals its subtopics.
 */
export default function FrameworkDiagram() {
  const t = useT();
  const [active, setActive] = useState("body");

  const labels = t("home.framework.labels");
  const subtopics = t("home.framework.subtopics");
  const center = t("home.framework.center");

  // Positions for three pillar nodes around the center (top, bottom-left, bottom-right)
  const NODES = {
    body: { x: 350, y: 120, anchor: "middle", labelOffset: -78 },
    mind: { x: 120, y: 460, anchor: "middle", labelOffset: 78 },
    spirit: { x: 580, y: 460, anchor: "middle", labelOffset: 78 },
  };
  const CENTER = { x: 350, y: 310 };

  return (
    <div className="relative w-full max-w-3xl mx-auto" data-testid="framework-diagram">
      <svg viewBox="0 0 700 600" className="w-full h-auto">
        <defs>
          <radialGradient id="centerGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="60%" stopColor="#F8F7F2" />
            <stop offset="100%" stopColor="#EBE8DF" />
          </radialGradient>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#B89B5E" stopOpacity="0.0" />
            <stop offset="50%" stopColor="#B89B5E" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#B89B5E" stopOpacity="0.0" />
          </linearGradient>
        </defs>

        {/* connection lines */}
        {Object.entries(NODES).map(([key, n]) => (
          <line
            key={key}
            x1={CENTER.x}
            y1={CENTER.y}
            x2={n.x}
            y2={n.y}
            stroke={active === key ? "#B89B5E" : "rgba(184,155,94,0.32)"}
            strokeWidth={active === key ? 1.5 : 0.8}
            strokeDasharray={active === key ? "0" : "4 5"}
            style={{ transition: "all 0.5s ease" }}
          />
        ))}

        {/* outer ring */}
        <circle
          cx={CENTER.x}
          cy={CENTER.y}
          r="200"
          fill="none"
          stroke="rgba(26,26,26,0.06)"
          strokeWidth="1"
        />
        <circle
          cx={CENTER.x}
          cy={CENTER.y}
          r="260"
          fill="none"
          stroke="rgba(26,26,26,0.04)"
          strokeWidth="1"
        />

        {/* center sanity node */}
        <circle
          cx={CENTER.x}
          cy={CENTER.y}
          r="78"
          fill="url(#centerGrad)"
          stroke="#B89B5E"
          strokeWidth="1.2"
        />
        <text
          x={CENTER.x}
          y={CENTER.y - 4}
          textAnchor="middle"
          fontFamily="Fraunces, serif"
          fontStyle="italic"
          fontSize="28"
          fill="#1A1A1A"
        >
          {center}
        </text>
        <text
          x={CENTER.x}
          y={CENTER.y + 22}
          textAnchor="middle"
          fontFamily="DM Sans, sans-serif"
          fontSize="9"
          letterSpacing="4"
          fill="#9A7E47"
        >
          THE METHOD
        </text>

        {/* pillar nodes */}
        {Object.entries(NODES).map(([key, n]) => {
          const isActive = active === key;
          const lbl = labels[key];
          const subs = subtopics[key] || [];
          return (
            <g
              key={key}
              onMouseEnter={() => setActive(key)}
              onFocus={() => setActive(key)}
              onClick={() => setActive(key)}
              style={{ cursor: "pointer" }}
              tabIndex={0}
              data-testid={`framework-pillar-${key}`}
            >
              <circle
                cx={n.x}
                cy={n.y}
                r={isActive ? 56 : 50}
                fill={isActive ? "#1A1A1A" : "#FFFFFF"}
                stroke={isActive ? "#B89B5E" : "rgba(26,26,26,0.12)"}
                strokeWidth={isActive ? 1.5 : 1}
                style={{ transition: "all 0.5s ease" }}
              />
              <text
                x={n.x}
                y={n.y - 2}
                textAnchor="middle"
                fontFamily="Fraunces, serif"
                fontSize="20"
                fontWeight="500"
                fill={isActive ? "#F8F7F2" : "#1A1A1A"}
                style={{ transition: "fill 0.4s ease" }}
              >
                {lbl.title}
              </text>
              <text
                x={n.x}
                y={n.y + 18}
                textAnchor="middle"
                fontFamily="DM Sans, sans-serif"
                fontSize="9"
                letterSpacing="3"
                fill={isActive ? "#D4BB85" : "#9A7E47"}
                style={{ transition: "fill 0.4s ease" }}
              >
                {String(lbl.sub).toUpperCase()}
              </text>

              {/* subtopic labels emanating outward */}
              {isActive &&
                subs.map((s, i) => {
                  // place subtopics on an arc outward from the node center
                  const baseAngle =
                    key === "body" ? -90 : key === "mind" ? 200 : -20;
                  const spread = 70;
                  const angle =
                    ((baseAngle - spread / 2 + (spread / (subs.length - 1)) * i) *
                      Math.PI) /
                    180;
                  const r = 130;
                  const sx = n.x + Math.cos(angle) * r;
                  const sy = n.y + Math.sin(angle) * r;
                  return (
                    <g key={s} style={{ opacity: 0.95 }}>
                      <line
                        x1={n.x + Math.cos(angle) * 60}
                        y1={n.y + Math.sin(angle) * 60}
                        x2={sx - Math.cos(angle) * 22}
                        y2={sy - Math.sin(angle) * 22}
                        stroke="#B89B5E"
                        strokeWidth="0.6"
                        strokeDasharray="2 3"
                      />
                      <text
                        x={sx}
                        y={sy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontFamily="DM Sans, sans-serif"
                        fontSize="11"
                        fontWeight="500"
                        fill="#2D2D2D"
                      >
                        {s}
                      </text>
                    </g>
                  );
                })}
            </g>
          );
        })}
      </svg>

      <div className="mt-8 flex items-center justify-center gap-3">
        {Object.entries(labels).map(([key, lbl]) => (
          <button
            key={key}
            type="button"
            onClick={() => setActive(key)}
            data-testid={`framework-pill-${key}`}
            className={`px-4 py-2 text-[0.7rem] uppercase tracking-[0.22em] border transition-colors ${
              active === key
                ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#F8F7F2]"
                : "border-[rgba(26,26,26,0.16)] text-[#4A4A4A] hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
            }`}
          >
            {lbl.title}
          </button>
        ))}
      </div>
    </div>
  );
}
